import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import serverConfig from "../../config/server";
import UserService from "../../services/UserService";

export type NotificationStatus = "PENDING" | "SENT" | "FAILED" | "RETRYING";
export type SourceSystem = "grafana-webhook" | "zammad-webhook" | "outline-webhook" | "discourse-webhook";

export interface NotificationRow {
  id: string;
  event_type: string;
  channel: string;
  source_system: string;
  source_software: string | null;
  title: string;
  body: string | null;
  recipient: string[] | null;
  metadata: Record<string, unknown> | null;
  idempotency_key: string | null;
  status: NotificationStatus;
  attempt_number: number;
  sent_at: string | null;
  created_at: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total_records: number;
  total_pages: number;
}

export interface NotificationListResponse {
  data: NotificationRow[];
  pagination: PaginationMeta;
}

export interface NotificationListParams {
  status?: string;
  source_system?: string;
  search?: string;
  page?: number;
  limit?: number;
}

async function fetchNotifications(params: NotificationListParams): Promise<NotificationListResponse> {
  const token = await UserService.ensureFreshToken();
  const res = await axios.post(`${serverConfig.api_base_url}notification_service_list_get`, {
    token,
    ...params,
  });
  if (res.data.status !== 200) {
    throw new Error(res.data.message || "Failed to load notifications");
  }
  return res.data.items as NotificationListResponse;
}

async function retryNotification(notificationId: string): Promise<void> {
  const token = await UserService.ensureFreshToken();
  const res = await axios.post(`${serverConfig.api_base_url}notification_service_retry_post`, {
    token,
    notification_id: notificationId,
  });
  if (res.data.status !== 200) {
    throw new Error(res.data.message || "Retry failed");
  }
}

export function useNotifications(params: NotificationListParams) {
  return useQuery({
    queryKey: ["notification-admin", "list", params],
    queryFn: () => fetchNotifications(params),
    placeholderData: (previous) => previous,
  });
}

export function useRetryNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: retryNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-admin", "list"] });
    },
  });
}
